import React from "react";
import type { AccountInfo } from "@azure/msal-browser";
import { getConsoleMsalInstance, CONSOLE_LOGIN_SCOPES } from "./msalInstance";
import { POST_LOGOUT_REDIRECT_URI } from "./msalRedirectUris";
import { isIdTokenStale, isIdTokenStillValid } from "./idTokenExpiry";
import {
  CONSOLE_SILENT_ACQUIRE_BASE_DELAY_MS,
  CONSOLE_SILENT_ACQUIRE_MAX_ATTEMPTS,
  CONSOLE_TOKEN_SKEW_MS,
} from "./sessionConstants";
import { startConsoleKeepAlive, stopConsoleKeepAlive } from "./sessionKeepAlive";
import { needsInteractiveRecovery, redirectForFreshConsoleSession } from "./sessionRecovery";
import { readSeededMsalIdToken } from "./msalPlaywrightSeed";
import type { TokenSourceOptions } from "../api/consoleApi";
import { AppBootScreen } from "../components/loading";

/** The sentence the refusal path renders; unchanged text from the console identity package. */
export const CONSOLE_REFUSAL_SENTENCE =
  "This sign-in was refused. Sign in again with your corporate account.";

/**
 * Internal renewal options. ``origin`` is deliberately not on the public
 * ``TokenSourceOptions``: the keep-alive is wired with a bound ``"timer"`` origin so
 * a background tick can never reach the interactive-redirect branch.
 */
type RenewOptions = TokenSourceOptions & { origin?: "user" | "timer" };

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Optional overrides for interactive sign-in (product AuthProvider.tsx:11-13). */
export type LoginOptions = {
  redirectStartPage?: string;
};

/**
 * Console sign-in, copied from the product SPA's AuthProvider (design.md section 11).
 *
 * The console signs the caller in against the corporate console app registration and
 * hands the console API the resulting id token. That token's audience is the console
 * client id and it carries the `roles` claim the sign-in projects, which is exactly
 * what cortex_console_token_validator accepts. A product-tenant token would be
 * refused as a wrong audience (spec.md 8C row NR-2), so there is no shared account
 * cache and no shared instance with the product app.
 */
export interface ConsoleAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  account: AccountInfo | null;
  error: string | null;
  login: (options?: LoginOptions) => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * The ID token the console API accepts, renewed silently when it is stale or when
   * ``forceRefresh`` is set; ``null`` with no account, or when renewal is gone and no
   * still-valid token is held. Never signs anyone out by itself.
   */
  getIdToken: (options?: TokenSourceOptions) => Promise<string | null>;
  /** Drop a local account whose token the console API will not accept. */
  releaseStaleSession: () => void;
}

export const ConsoleAuthContext = React.createContext<ConsoleAuthState | undefined>(
  undefined,
);

export const ConsoleAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [account, setAccount] = React.useState<AccountInfo | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const initialise = async () => {
      try {
        const seededBeforeInit = readSeededMsalIdToken();
        const msal = getConsoleMsalInstance();
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        if (cancelled) {
          return;
        }
        const signedIn = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
        setAccount(signedIn);
        // Playwright UAT harness seeds an id_token in sessionStorage (msalPlaywrightSeed.ts,
        // gated to localhost + the harness marker); treat that as signed-in the same way the
        // product SPA's AuthProvider already does, so tenant-theme/API calls behind
        // isAuthenticated are not skipped just because there is no real MSAL account.
        setIsAuthenticated(Boolean(signedIn) || Boolean(seededBeforeInit || readSeededMsalIdToken()));
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Sign-in could not start.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    void initialise();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = React.useCallback(async (options?: LoginOptions) => {
    try {
      setError(null);
      await getConsoleMsalInstance().loginRedirect({
        scopes: CONSOLE_LOGIN_SCOPES,
        redirectStartPage: options?.redirectStartPage ?? window.location.href,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    }
  }, []);

  const signIn = React.useCallback(async () => {
    await login();
  }, [login]);

  const releaseStaleSession = React.useCallback(() => {
    stopConsoleKeepAlive();
    setAccount(null);
    setIsAuthenticated(false);
  }, []);

  const signOut = React.useCallback(async () => {
    try {
      setError(null);
      stopConsoleKeepAlive();
      await getConsoleMsalInstance().logoutRedirect({
        postLogoutRedirectUri: POST_LOGOUT_REDIRECT_URI,
      });
      setAccount(null);
      setIsAuthenticated(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-out failed.");
    }
  }, []);

  /**
   * Freshness-driven silent renewal (design.md section 5.2).
   *
   * MSAL's silent flow hands back the cached ID token whenever the access token is
   * still alive, so the console reads ``exp`` itself and forces a refresh when the
   * token is within the skew margin. Attempts are bounded; a still-valid token
   * survives a failed renewal; only a user-initiated call that ends in an
   * interaction-required error goes to Microsoft, and never from the timer.
   */
  const renewIdToken = React.useCallback(
    async (options: RenewOptions = {}): Promise<string | null> => {
      // DP-CDAM-03 (founder ruling, 2026-09-22): the Playwright-UAT harness-seed
      // bypass (msalPlaywrightSeed.ts) is consulted before any real MSAL call so a
      // headless UAT walk's TenantThemeProvider readiness gate and token reads can
      // complete without a renewable AAD session. The helper itself refuses off
      // localhost and without the explicit harness marker, so this is inert outside
      // an active Playwright-UAT run.
      const seeded = readSeededMsalIdToken();
      if (seeded) {
        return seeded;
      }
      if (!account) {
        return null;
      }
      const msal = getConsoleMsalInstance();
      const origin = options.origin ?? "user";
      const held =
        msal.getAllAccounts().find((a) => a.homeAccountId === account.homeAccountId) ??
        account;
      const cached = held.idToken ?? null;
      const now = Date.now();
      const stale =
        Boolean(options.forceRefresh) || isIdTokenStale(cached, now, CONSOLE_TOKEN_SKEW_MS);

      let lastError: unknown = null;
      for (let attempt = 0; attempt < CONSOLE_SILENT_ACQUIRE_MAX_ATTEMPTS; attempt += 1) {
        try {
          const result = await msal.acquireTokenSilent({
            scopes: CONSOLE_LOGIN_SCOPES,
            account: held,
            forceRefresh: stale,
          });
          return result.idToken ?? null;
        } catch (err) {
          lastError = err;
          if (attempt < CONSOLE_SILENT_ACQUIRE_MAX_ATTEMPTS - 1) {
            await sleep(CONSOLE_SILENT_ACQUIRE_BASE_DELAY_MS * 2 ** attempt);
          }
        }
      }

      if (origin === "user" && needsInteractiveRecovery(lastError)) {
        try {
          await redirectForFreshConsoleSession(window.location.href);
        } catch (err) {
          console.error("[console session] interactive recovery could not start", err);
          releaseStaleSession();
          setError(CONSOLE_REFUSAL_SENTENCE);
        }
        return null;
      }

      console.warn("[console session] silent renewal failed after bounded retries");
      // Safe failure: the request goes ahead with the token still in hand, if it is
      // genuinely still valid; otherwise there is nothing to send. Nobody is signed out.
      return isIdTokenStillValid(cached, Date.now()) ? cached : null;
    },
    [account, releaseStaleSession],
  );

  const getIdToken = React.useCallback(
    (options?: TokenSourceOptions): Promise<string | null> => {
      // Mirror the product SPA's AuthProvider.getAccessToken: a seeded UAT token always
      // wins, since there is no real MSAL account to renew against under the harness.
      const seeded = readSeededMsalIdToken();
      if (seeded) {
        return Promise.resolve(seeded);
      }
      return renewIdToken({ forceRefresh: options?.forceRefresh, origin: "user" });
    },
    [renewIdToken],
  );

  React.useEffect(() => {
    if (!account) {
      stopConsoleKeepAlive();
      return undefined;
    }
    startConsoleKeepAlive(() => renewIdToken({ forceRefresh: true, origin: "timer" }));
    return () => {
      stopConsoleKeepAlive();
    };
  }, [account, renewIdToken]);

  const value: ConsoleAuthState = {
    isAuthenticated,
    isLoading,
    account,
    error,
    login,
    signIn,
    signOut,
    getIdToken,
    releaseStaleSession,
  };

  if (isLoading) {
    return (
      <AppBootScreen
        title="Loading Cortex…"
        data-testid="console-auth-loading"
      />
    );
  }

  return (
    <ConsoleAuthContext.Provider value={value}>{children}</ConsoleAuthContext.Provider>
  );
};

export function useConsoleAuth(): ConsoleAuthState {
  const context = React.useContext(ConsoleAuthContext);
  if (context === undefined) {
    throw new Error("useConsoleAuth must be used within a ConsoleAuthProvider");
  }
  return context;
}

/** Product-shaped alias so copied Login / ProtectedRoute keep `useAuth`. */
export function useAuth() {
  return useConsoleAuth();
}
